import ColorPicker from '@/components/commons/color-picker';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ThemeBadgeType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

export const DEFAULT_THEME_BADGE: ThemeBadgeType = {
  position: 'outside',
  positionOnCard: 'bottom_left',
  borderCornerRadius: 24,
  soldOutText: 'Sold Out',
  soldOutColorScheme: DEFAULT_THEME_COLOR_SCHEME,
  promotionText: 'Discount',
  promotionColorScheme: DEFAULT_THEME_COLOR_SCHEME,
  promotionColorGradient: {
    text: '#000000',
    color1: '#4C0F0F',
    color2: '#B22222',
  },
  newArrivalText: 'New Arrival',
  newArrivalColorScheme: DEFAULT_THEME_COLOR_SCHEME,
  newArrivalColorGradient: {
    text: '#000000',
    color1: '#4C0F0F',
    color2: '#B22222',
  },
  bestSellerText: 'Best Seller',
  bestSellerColorScheme: DEFAULT_THEME_COLOR_SCHEME,
  bestSellerColorGradient: {
    text: '#000000',
    color1: '#4C0F0F',
    color2: '#B22222',
  },
};

export default function ThemeBadgeSetting() {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const badge = builder.theme?.badge || DEFAULT_THEME_BADGE;

  const setValue = (value: Partial<ThemeBadgeType>) => {
    builder.setTheme({
      badge: { ...badge, ...value },
    });
  };

  return (
    <div>
      <span className='mb-2 flex'>Position</span>
      <InputTab
        defaultValue={badge.position || DEFAULT_THEME_BADGE.position}
        grid={2}
        options={[
          { label: 'Outside', value: 'outside' },
          { label: 'Inside', value: 'inside' },
        ]}
        onChange={(val) => setValue({ position: val })}
      />
      {badge.position !== 'inside' && (
        <>
          <div className='mb-2 mt-4'>Position on cards</div>
          <div className='mb-4'>
            <Select
              name='positionOnCard'
              defaultValue={DEFAULT_THEME_BADGE.positionOnCard}
              value={badge.positionOnCard}
              onValueChange={(val) => setValue({ positionOnCard: val })}
            >
              <SelectTrigger className='py-5'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent side='bottom' className='max-h-48 overflow-y-auto'>
                <SelectItem value='bottom_left'>Bottom left</SelectItem>
                <SelectItem value='bottom_right'>Bottom right</SelectItem>
                <SelectItem value='top_left'>Top left</SelectItem>
                <SelectItem value='top_right'>Top right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      <InputSlider
        label='Corner radius'
        value={badge.borderCornerRadius || DEFAULT_THEME_BADGE.borderCornerRadius}
        max={100}
        min={0}
        onChange={(val) => setValue({ borderCornerRadius: val })}
        rightContent={<div>px</div>}
      />
      <h2 className='mb-4 mt-8 flex text-base font-bold'>Promotion</h2>
      <div className='mb-2 mt-4 text-sm'>Promotion badge text</div>
      <Input
        name='promotionText'
        defaultValue={DEFAULT_THEME_BADGE.promotionText}
        value={badge.promotionText}
        onChange={(e) => {
          setValue({
            promotionText: e.target.value,
          });
        }}
      />
      <div className='mb-2 mt-4 text-sm'>Promotion badge color scheme</div>
      {badge.position === 'inside' ? (
        <div className='mt-4 flex flex-col gap-4'>
          <div>
            <span className='mb-2 flex'>Text color</span>
            <ColorPicker
              color={badge.promotionColorGradient?.text}
              inputColor={badge.promotionColorGradient?.text}
              onChangeColor={(color) => {
                setValue({
                  promotionColorGradient: {
                    ...badge.promotionColorGradient,
                    text: color.hex,
                  },
                });
              }}
              isShowPercentage={false}
            />
          </div>
          <div className='flex items-center justify-between gap-4'>
            <div>
              <span className='mb-2 flex'>Background left</span>
              <ColorPicker
                color={badge.promotionColorGradient?.color1}
                inputColor={badge.promotionColorGradient?.color1}
                onChangeColor={(color) => {
                  setValue({
                    promotionColorGradient: {
                      ...badge.promotionColorGradient,
                      color1: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
            <div>
              <span className='mb-2 flex'>Background right</span>
              <ColorPicker
                color={badge.promotionColorGradient?.color2}
                inputColor={badge.promotionColorGradient?.color2}
                onChangeColor={(color) => {
                  setValue({
                    promotionColorGradient: {
                      ...badge.promotionColorGradient,
                      color2: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <SchemeSetting
            value={badge.promotionColorScheme}
            onChange={(theme) => {
              setValue({
                promotionColorScheme: theme,
              });
            }}
          />
          <div className='mt-1 text-xs'>
            To edit all your theme&apos;s colors, go to your color theme settings.
          </div>
        </>
      )}
      <h2 className='mb-4 mt-8 flex text-base font-bold'>Sold out</h2>
      <div className='mb-2 mt-4 text-sm'>Sold out badge text</div>
      <Input
        name='soldOutText'
        defaultValue={DEFAULT_THEME_BADGE.soldOutText}
        value={badge.soldOutText}
        maxLength={30}
        onChange={(e) => {
          setValue({
            soldOutText: e.target.value,
          });
        }}
      />
      {badge.position !== 'inside' && (
        <>
          <div className='mb-2 mt-4 text-sm'>Sold out badge color scheme</div>
          <SchemeSetting
            value={badge.soldOutColorScheme}
            onChange={(theme) => {
              setValue({
                soldOutColorScheme: theme,
              });
            }}
          />
          <div className='mt-1 text-xs'>
            To edit all your theme&apos;s colors, go to your color theme settings.
          </div>
        </>
      )}
      <h2 className='mb-4 mt-8 flex text-base font-bold'>New arrival</h2>
      <div className='mb-2 mt-4 text-sm'>New arrival badge text</div>
      <Input
        name='newArrivalText'
        defaultValue={DEFAULT_THEME_BADGE.newArrivalText}
        value={badge.newArrivalText}
        maxLength={30}
        onChange={(e) => {
          setValue({
            newArrivalText: e.target.value,
          });
        }}
      />
      {badge.position === 'inside' ? (
        <div className='mt-4 flex flex-col gap-4'>
          <div>
            <span className='mb-2 flex'>Text color</span>
            <ColorPicker
              color={badge.newArrivalColorGradient?.text}
              inputColor={badge.newArrivalColorGradient?.text}
              onChangeColor={(color) => {
                setValue({
                  newArrivalColorGradient: {
                    ...badge.newArrivalColorGradient,
                    text: color.hex,
                  },
                });
              }}
              isShowPercentage={false}
            />
          </div>
          <div className='flex items-center justify-between gap-4'>
            <div>
              <span className='mb-2 flex'>Background left</span>
              <ColorPicker
                color={badge.newArrivalColorGradient?.color1}
                inputColor={badge.newArrivalColorGradient?.color1}
                onChangeColor={(color) => {
                  setValue({
                    newArrivalColorGradient: {
                      ...badge.newArrivalColorGradient,
                      color1: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
            <div>
              <span className='mb-2 flex'>Background right</span>
              <ColorPicker
                color={badge.newArrivalColorGradient?.color2}
                inputColor={badge.newArrivalColorGradient?.color2}
                onChangeColor={(color) => {
                  setValue({
                    newArrivalColorGradient: {
                      ...badge.newArrivalColorGradient,
                      color2: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className='mb-2 mt-4 text-sm'>New arrival badge color scheme</div>
          <SchemeSetting
            value={badge.newArrivalColorScheme}
            onChange={(theme) => {
              setValue({
                newArrivalColorScheme: theme,
              });
            }}
          />
          <div className='mt-1 text-xs'>
            To edit all your theme&apos;s colors, go to your color theme settings.
          </div>
        </>
      )}
      <h2 className='mb-4 mt-8 flex text-base font-bold'>Best seller</h2>
      <div className='mb-2 mt-4 text-sm'>Best seller badge text</div>
      <Input
        name='bestSellerText'
        defaultValue={DEFAULT_THEME_BADGE.bestSellerText}
        value={badge.bestSellerText}
        maxLength={30}
        onChange={(e) => {
          setValue({
            bestSellerText: e.target.value,
          });
        }}
      />
      {badge.position === 'inside' ? (
        <div className='mt-4 flex flex-col gap-4'>
          <div>
            <span className='mb-2 flex'>Text color</span>
            <ColorPicker
              color={badge.bestSellerColorGradient?.text}
              inputColor={badge.bestSellerColorGradient?.text}
              onChangeColor={(color) => {
                setValue({
                  bestSellerColorGradient: {
                    ...badge.bestSellerColorGradient,
                    text: color.hex,
                  },
                });
              }}
              isShowPercentage={false}
            />
          </div>
          <div className='flex items-center justify-between gap-4'>
            <div>
              <span className='mb-2 flex'>Background left</span>
              <ColorPicker
                color={badge.bestSellerColorGradient?.color1}
                inputColor={badge.bestSellerColorGradient?.color1}
                onChangeColor={(color) => {
                  setValue({
                    bestSellerColorGradient: {
                      ...badge.bestSellerColorGradient,
                      color1: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
            <div>
              <span className='mb-2 flex'>Background right</span>
              <ColorPicker
                color={badge.bestSellerColorGradient?.color2}
                inputColor={badge.bestSellerColorGradient?.color2}
                onChangeColor={(color) => {
                  setValue({
                    bestSellerColorGradient: {
                      ...badge.bestSellerColorGradient,
                      color2: color.hex,
                    },
                  });
                }}
                isShowInputColor={false}
                isShowPercentage={false}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className='mb-2 mt-4 text-sm'>Best seller badge color scheme</div>
          <SchemeSetting
            value={badge.bestSellerColorScheme}
            onChange={(theme) => {
              setValue({
                bestSellerColorScheme: theme,
              });
            }}
          />
          <div className='mt-1 text-xs'>
            To edit all your theme&apos;s colors, go to your color theme settings.
          </div>
        </>
      )}
    </div>
  );
}
